import { alpha } from '@mui/material/styles';
import { amber, yellow } from '@mui/material/colors';

// Shared grid row height (px) for the entity list view and the generated child grids, so every
// grid has the same density. A bit tighter than MUI's compact default. Change here to retune all grids.
export const GRID_ROW_HEIGHT = 44;

// Shared DataGrid styling used by the entity list view and the generated child grids
// so they look identical. The row checkbox and the row-edit pencil (actions column) are
// toned down and hidden (opacity 0) until the row is hovered or the control is focused
// (keyboard); a checked box stays visible and tinted, matching the header select-all.
// Spread this into a DataGrid `sx` alongside any per-grid layout keys.
export const hoverRevealGridSx = {
  '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root': {
    opacity: 0,
    transition: 'opacity 0.1s',
    color: 'action.disabled',
  },
  '& .MuiDataGrid-row:hover .MuiCheckbox-root': { opacity: 1 },
  '& .MuiDataGrid-cellCheckbox:focus-within .MuiCheckbox-root': { opacity: 1 },
  '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root.Mui-checked': {
    opacity: 1,
    color: 'primary.main',
  },
  // The header select-all is toned down until it's actually in use.
  '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': {
    color: 'action.disabled',
  },
  '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.Mui-checked, & .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.MuiCheckbox-indeterminate':
    { color: 'primary.main' },
  // The row-edit pencil is hidden until the row is hovered or the action button is focused.
  '& .MuiDataGrid-actionsCell': {
    opacity: 0,
    transition: 'opacity 0.1s',
  },
  '& .MuiDataGrid-row:hover .MuiDataGrid-actionsCell': { opacity: 1 },
  '& .MuiDataGrid-actionsCell:focus-within': { opacity: 1 },
};

// "Not saved yet" — a row of a child grid, a control of a form (new or edited, waiting for Save):
// a faint yellow tint and a left bar, so colour is never the only signal. The theme has no yellow
// token (its warning is orange), and this marks "not saved yet", not a warning.
export const UNSAVED_SX = {
  backgroundColor: alpha(yellow[600], 0.18),
  boxShadow: `inset 3px 0 0 ${amber[600]}`,
};

// A row with unsaved changes in a child grid.
export const UNSAVED_ROW_SX = {
  ...UNSAVED_SX,
  '&:hover': { backgroundColor: alpha(yellow[600], 0.28) },
  // A row in edit mode paints every cell opaque white; let the tint through (the inputs keep theirs).
  '&.MuiDataGrid-row--editing .MuiDataGrid-cell': { backgroundColor: 'transparent' },
};

/** Per-row sx marking these rows unsaved — by row id, like the copied-row marking, so no getRowClassName. */
export function unsavedRowsSx(ids: ReadonlySet<string> | undefined): Record<string, unknown> {
  const sx: Record<string, unknown> = {};
  ids?.forEach((id) => {
    sx[`& .MuiDataGrid-row[data-id="${id}"]`] = UNSAVED_ROW_SX;
  });
  return sx;
}
